import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import {
  Box,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material';
import React, { ChangeEvent, FC, MouseEvent, useContext, useState } from 'react';
import { IMonitoringJob } from '../../shared/interfaces/IMonitoringJob';
import MonitoringJobItem from '../MonitoringJobItem/MonitoringJobItem';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarIcon from '@mui/icons-material/EventRepeat';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import { Check, Close, InfoOutlined } from '@mui/icons-material';
import Helper from '../../shared/helpers/Helper';
import { Trans, useTranslation } from 'react-i18next';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import SliderStarsAsLabels from '../Sliders/StarsAsLabels/SliderStarsAsLabels';
import { ReactComponent as FlagDe } from '../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../assets/images/flags/gb.svg';
import { ReactComponent as FlagNl } from '../../assets/images/flags/nl.svg';
import { constants } from '../../constants/constants';
import { AuthContext } from '../../providers/AuthProvider';
import Confirm from '../Confirm/Confirm';

interface Props {
  id: string;
  companyName: string;
  label: string;
  createdAt: any;
  updatedAt: string;
  jobs: IMonitoringJob[];
  url?: string;
  communitySettings: CommunityManagement;
  platformId: number;
  reload: () => Promise<void>;
}

const EditMonitoringCard: FC<Props> = ({
  id,
  companyName,
  label,
  createdAt,
  updatedAt,
  url,
  jobs,
  communitySettings,
  reload,
  platformId,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);

  const [edit, setEdit] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [newLabel, setNewLabel] = useState(label);
  const [newUrl, setNewUrl] = useState(url);
  const [newCommunitySettings, setNewCommunitySettings] =
    useState<CommunityManagement>(communitySettings);

  const deleteMonitoring = async () => {
    const { success } = await ReviewMonitoringService.deleteMonitoring(id);
    if (!success) return;
    await reload();
  };

  const updateMonitoringLabel = async () => {
    if (!newUrl) return;
    if (!newCommunitySettings) return;

    const updatedLabel = await ReviewMonitoringService.updateMonitoringLabel(
      id,
      newLabel,
      newCommunitySettings
    );

    await ReviewMonitoringService.updateMonitoringScheduler(id);

    if (updatedLabel === undefined) {
      setNewLabel(label);
      setNewCommunitySettings(communitySettings);
      setEdit(false);
      return;
    }

    if (newLabel === updatedLabel) setEdit(false);

    await reload();
  };

  const handleScoreThresholdChange = (event: Event, newValue: number | number[]) => {
    setNewCommunitySettings({
      ...newCommunitySettings,
      scoreThreshold: newValue as number,
    });
  };

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewCommunitySettings({
      ...newCommunitySettings,
      useUsername: event.target.checked,
    });
  };

  return (
    <Card
      sx={{
        boxShadow: 'unset',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        justifyContent: 'space-between',
        overflow: 'auto',
      }}
    >
      <CardHeader
        sx={{ display: 'flex', flexDirection: 'column-reverse' }}
        title={
          <>
            {!edit ? (
              <CardContent
                sx={{
                  p: theme.spacing(2),
                  maxHeight: 350,
                }}
              >
                {jobs.map((jobItem) => {
                  return (
                    <MonitoringJobItem
                      key={jobItem.jobId}
                      uuid={jobItem.jobId}
                      type={jobItem.type}
                      kind={jobItem.kind}
                      url={url}
                      label={label}
                      company={companyName}
                      platformId={platformId}
                    />
                  );
                })}
              </CardContent>
            ) : (
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  defaultValue={newLabel}
                  label={t('label')}
                  sx={{ mb: theme.spacing(2), mt: theme.spacing(3) }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setNewLabel(event.target.value)
                  }
                />
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mb: theme.spacing(2) }}
                >
                  <InputLabel id={t('tonality')}>{t('tonality')}</InputLabel>
                  <Select
                    value={newCommunitySettings.tonality.preference === 0 ? '0' : '1'}
                    label={t('tonality')}
                    labelId={t('tonality')}
                    onChange={(event: SelectChangeEvent) =>
                      setNewCommunitySettings({
                        ...newCommunitySettings,
                        tonality: {
                          ...newCommunitySettings.tonality,
                          preference: parseInt(event.target.value),
                        },
                      })
                    }
                  >
                    <MenuItem value={'0'}>{t('tonality_personal')}</MenuItem>
                    <MenuItem value={'1'}>{t('tonality_formal')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mb: theme.spacing(2) }}
                >
                  <InputLabel id={t('pronoun')}>{t('pronoun')}</InputLabel>
                  <Select
                    value={newCommunitySettings.pronoun.preference === 0 ? '0' : '1'}
                    labelId={t('pronoun')}
                    label={t('pronoun')}
                    onChange={(event: SelectChangeEvent) =>
                      setNewCommunitySettings({
                        ...newCommunitySettings,
                        pronoun: {
                          ...newCommunitySettings.pronoun,
                          preference: parseInt(event.target.value),
                        },
                      })
                    }
                  >
                    {constants.communityManagement.pronounOptions.map((pronoun, index) => {
                      return (
                        <MenuItem
                          key={index}
                          value={index}
                        >
                          <Trans t={t}>{pronoun}</Trans>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  value={newCommunitySettings.salutationPrefix}
                  label={t('salutation')}
                  sx={{ mb: theme.spacing(2) }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setNewCommunitySettings({
                      ...newCommunitySettings,
                      salutationPrefix: event.target.value,
                    })
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={newCommunitySettings.greeting}
                  label={t('greeting')}
                  sx={{ mb: theme.spacing(2) }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setNewCommunitySettings({
                      ...newCommunitySettings,
                      greeting: event.target.value,
                    })
                  }
                />
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={newCommunitySettings.useUsername}
                        onChange={handleUsernameChange}
                      />
                    }
                    label={t('use_username')}
                    sx={{ mb: theme.spacing(2) }}
                  />
                </FormGroup>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                    mb: theme.spacing(2),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={newCommunitySettings.languagePreference}
                    exclusive
                    onChange={(event: MouseEvent<HTMLElement>, value: string) => {
                      if (value) {
                        setNewCommunitySettings({
                          ...newCommunitySettings,
                          languagePreference: value as Language,
                        });
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="nl">
                      <FlagNl
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <SliderStarsAsLabels
                  value={newCommunitySettings.scoreThreshold}
                  onChange={handleScoreThresholdChange}
                  sx={{ mt: theme.spacing(1), mb: theme.spacing(3) }}
                />
              </Box>
            )}
          </>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">
                    {t('created')} {Helper.getFormattedDate(createdAt, false)}
                  </Typography>
                  <Typography variant="body2">
                    {t('refreshed')} {Helper.getFormattedDate(updatedAt, false)}
                  </Typography>
                </Box>
              }
              arrow
            >
              <InfoOutlined
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
            </Tooltip>
            {!edit ? (
              <IconButton
                sx={{ mx: theme.spacing(1) }}
                size="small"
                onClick={() => setEdit(true)}
              >
                <EditIcon
                  sx={{ color: theme.palette.primary.main }}
                  fontSize="small"
                />
              </IconButton>
            ) : (
              <IconButton
                sx={{ mx: theme.spacing(1) }}
                size="small"
                onClick={() => updateMonitoringLabel()}
              >
                <Check
                  sx={{ color: theme.palette.primary.main }}
                  fontSize="small"
                />
              </IconButton>
            )}
            {!edit && hasRole('can-service-backdated') && (
              <IconButton
                size="small"
                onClick={async () => {
                  const { success } = await ReviewMonitoringService.serviceBackdated(id);
                  if (!success) return;
                  await reload();
                }}
              >
                <CalendarIcon
                  fontSize="small"
                  sx={{ color: theme.palette.primary.main }}
                />
              </IconButton>
            )}
            {!edit ? (
              <>
                <IconButton
                  size="small"
                  onClick={() => setDeleteIsOpen(true)}
                >
                  <DeleteIcon
                    color="error"
                    fontSize="small"
                  />
                </IconButton>
                <Confirm
                  text={t('delete_confirm')}
                  open={deleteIsOpen}
                  onCancel={() => setDeleteIsOpen(false)}
                  onOk={() => deleteMonitoring()}
                />
              </>
            ) : (
              <IconButton
                size="small"
                onClick={() => {
                  setNewLabel(label);
                  setEdit(false);
                }}
              >
                <Close
                  color="error"
                  fontSize="small"
                />
              </IconButton>
            )}
          </Box>
        }
      />
    </Card>
  );
};

export default EditMonitoringCard;
